class BlogPostActions extends HTMLElement {
	connectedCallback() {
		this.articleId = this.dataset.articleId || "";
		this.articleTitle = this.dataset.articleTitle || document.title || "";
		this.articleUrl = this.dataset.articleUrl || window.location.href;

		this.shareToggle = this.querySelector("[data-blogpost-share-toggle]");
		this.shareMenu = this.querySelector("[data-blogpost-share-menu]");

		this.#bindEvents();
	}

	#bindEvents() {
		if (this.shareToggle && this.shareMenu) {
			this.shareToggle.addEventListener("click", (event) => {
				event.preventDefault();
				this.#toggleShareMenu();
			});

			this.shareMenu.addEventListener("click", (event) => this.#handleShareMenuClick(event));

			document.addEventListener("click", (event) => {
				const target = event.target;
				if (!this.contains(target)) {
					this.#closeShareMenu();
				}
			});
		}
	}

	#toggleShareMenu() {
		if (!this.shareMenu) return;

		const isOpen = this.shareMenu.getAttribute("data-open") === "true";
		if (isOpen) {
			this.#closeShareMenu();
		} else {
			this.shareMenu.setAttribute("data-open", "true");
			this.shareMenu.removeAttribute("hidden");
		}
	}

	#closeShareMenu() {
		if (!this.shareMenu) return;

		this.shareMenu.setAttribute("data-open", "false");
		this.shareMenu.setAttribute("hidden", "hidden");
	}

	async #handleShareMenuClick(event) {
		const target = event.target;
		const item = target.closest("[data-share-action]");
		if (!item) return;

		const action = item.getAttribute("data-share-action");

		try {
			switch (action) {
				case "copy":
					await this.#copyLink();
					break;
				case "twitter":
					this.#shareToTwitter();
					break;
				case "facebook":
					this.#shareToFacebook();
					break;
				case "linkedin":
					this.#shareToLinkedIn();
					break;
				default:
					break;
			}
		} catch (_error) {
			// ignore share errors
		} finally {
			if (action !== "copy") {
				this.#closeShareMenu();
			}
		}
	}

	async #copyLink() {
		const url = this.articleUrl;
		if (!url) return;

		if (navigator.clipboard && navigator.clipboard.writeText) {
			await navigator.clipboard.writeText(url);
			return;
		}

		const input = document.createElement("input");
		input.type = "text";
		input.value = url;
		input.setAttribute("readonly", "readonly");
		input.style.position = "absolute";
		input.style.left = "-9999px";

		document.body.appendChild(input);
		input.select();

		try {
			document.execCommand("copy");
		} catch (_error) {
			// ignore unsupported copy
		}

		document.body.removeChild(input);
	}

	#openShareWindow(url) {
		const left = window.screenX + (window.outerWidth - 600) / 2;
		const top = window.screenY + (window.outerHeight - 600) / 2;
		const features = `width=600,height=600,left=${left},top=${top},noopener,noreferrer`;

		window.open(url, "_blank", features);
	}

	#shareToTwitter() {
		const encodedUrl = encodeURIComponent(this.articleUrl);
		const encodedText = encodeURIComponent(this.articleTitle);
		const shareUrl = `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedText}`;
		this.#openShareWindow(shareUrl);
	}

	#shareToFacebook() {
		const encodedUrl = encodeURIComponent(this.articleUrl);
		const shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`;
		this.#openShareWindow(shareUrl);
	}

	#shareToLinkedIn() {
		const encodedUrl = encodeURIComponent(this.articleUrl);
		const shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`;
		this.#openShareWindow(shareUrl);
	}
}

class BlogPostToc extends HTMLElement {
	connectedCallback() {
		const contentSelector = this.dataset.contentSelector;
		if (!contentSelector) return;

		const section = this.closest(".section");
		const root = section || document;
		const contentElement = root.querySelector(contentSelector);
		if (!contentElement) return;

		const levelsRaw = (this.dataset.levels || "2,3,4").trim();
		const pieces = levelsRaw
			.split(",")
			.map((value) => value.trim())
			.filter((value) => value.length > 0);
		const levels = new Set(pieces);

		const levelOrder = ["2", "3", "4", "5", "6"];
		const selectors = levelOrder
			.filter((level) => levels.has(level))
			.map((level) => `h${level}`)
			.join(",");

		if (!selectors) return;

		const headings = Array.from(contentElement.querySelectorAll(selectors)).filter(
			(heading) => {
				const text = heading.textContent || "";
				return text.trim().length > 0;
			},
		);

		const nav = this.querySelector("nav");
		if (!nav) return;

		nav.innerHTML = "";

		if (!headings.length) {
			const empty = document.createElement("div");
			empty.className = "text-slate-500 text-sm";
			empty.textContent = this.dataset.emptyText || "No sections";
			nav.appendChild(empty);
			return;
		}

		const makeSlug = (text) => {
			const lower = text.toLowerCase().trim();
			const noQuotes = lower.replace(/['"]/g, "");
			const dashed = noQuotes.replace(/[^a-z0-9]+/g, "-");
			const trimmed = dashed.replace(/^-+|-+$/g, "");
			return trimmed || "section";
		};

		const used = new Map();
		const ensureId = (heading) => {
			const existing = heading.getAttribute("id");
			if (existing) return existing;

			const base = makeSlug(heading.textContent || "");
			const previousCount = used.get(base) || 0;
			const nextCount = previousCount + 1;
			used.set(base, nextCount);

			const id = nextCount === 1 ? base : `${base}-${nextCount}`;
			heading.setAttribute("id", id);
			return id;
		};

		for (const heading of headings) {
			const id = ensureId(heading);
			const tagName = heading.tagName || "";
			const levelNumber = Number(tagName.replace("H", ""));
			const padding = Math.max(0, (levelNumber - 2) * 12);

			const link = document.createElement("a");
			link.href = `#${id}`;
			link.className =
				"group tflex items-start gap-2 bg-orange-50 px-3 py-2 rounded-lg w-full font-semibold text-orange-600 text-sm text-left transition-all";
			link.style.paddingLeft = `${padding + 12}px`;

			const icon = document.createElementNS("http://www.w3.org/2000/svg", "svg");
			icon.setAttribute("xmlns", "http://www.w3.org/2000/svg");
			icon.setAttribute("width", "24");
			icon.setAttribute("height", "24");
			icon.setAttribute("viewBox", "0 0 24 24");
			icon.setAttribute("fill", "none");
			icon.setAttribute("stroke", "currentColor");
			icon.setAttribute("stroke-width", "2");
			icon.setAttribute("stroke-linecap", "round");
			icon.setAttribute("stroke-linejoin", "round");
			icon.setAttribute("aria-hidden", "true");
			icon.setAttribute(
				"class",
				"lucide-chevron-right mt-0.5 w-4 h-4 transition-transform translate-x-1 lucide shrink-0",
			);

			const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
			path.setAttribute("d", "m9 18 6-6-6-6");
			icon.appendChild(path);

			const span = document.createElement("span");
			span.className = "line-clamp-2";
			span.textContent = (heading.textContent || "").trim();

			link.appendChild(icon);
			link.appendChild(span);
			nav.appendChild(link);
		}
	}
}

if (!customElements.get("blog-post-actions")) {
	customElements.define("blog-post-actions", BlogPostActions);
}

if (!customElements.get("blog-post-toc")) {
	customElements.define("blog-post-toc", BlogPostToc);
}
